import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { userDetailQueryOptions } from '@/features/users/api/users.api';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { toGeoFilterValues } from '@/utils/filters';
import type { AssignedInstitutesParams } from '../types/userInstituteMapping.types';
import { toMappingFilterSearch, toMappingFilterValues } from '../utils/mappingFilters';

const route = getRouteApi('/_auth/settings/institutes/user-mapping');

const ASSIGNED_PAGE_SIZE = 20;

export type ActiveTab = 'current' | 'assign';

type FilterValues = Record<string, string>;

const normalizeFilterValues = (values: FilterValues) =>
  toMappingFilterValues(toMappingFilterSearch(values));

export function useUserMappingFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const queryClient = useQueryClient();

  const activeTab: ActiveTab = search.tab ?? 'current';

  const appliedValues = useMemo(() => toMappingFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toMappingFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const { filterValues, handleFilterChange, updateFilterValues } = filters;

  const fillGeoFromUser = useCallback(
    async (userId: string) => {
      const user = await queryClient.fetchQuery(userDetailQueryOptions(userId));
      const userGeoValues = toGeoFilterValues({
        divisionId: user.divisionId ?? undefined,
        districtId: user.districtId ?? undefined,
        thanaId: user.thanaId ?? undefined,
      });
      updateFilterValues((previous) =>
        previous.userId === userId ? { ...previous, ...userGeoValues } : previous,
      );
    },
    [queryClient, updateFilterValues],
  );

  const handleMappingFilterChange = useCallback(
    (nextValues: FilterValues) => {
      const pickedUserId = nextValues.userId ?? '';
      const hasPickedNewUser = pickedUserId !== '' && pickedUserId !== filterValues.userId;
      handleFilterChange(nextValues);
      if (hasPickedNewUser) void fillGeoFromUser(pickedUserId).catch(() => undefined);
    },
    [filterValues.userId, handleFilterChange, fillGeoFromUser],
  );

  const institutesQueryParams = useMemo<Omit<AssignedInstitutesParams, 'page'> | null>(() => {
    const { userId, divisionId, districtId } = search;
    if (userId === undefined || divisionId === undefined || districtId === undefined) {
      return null;
    }
    return {
      userId,
      instituteTypeId: search.instituteTypeId,
      thanaId: search.thanaId,
      instituteId: search.instituteId,
      eiinNumber: search.eiinNumber,
      name: search.name,
      limit: ASSIGNED_PAGE_SIZE,
    };
  }, [search]);

  const handleTabChange = useCallback(
    (next: ActiveTab) => {
      void navigate({ search: (previous) => ({ ...previous, tab: next }) });
    },
    [navigate],
  );

  return {
    ...filters,
    handleFilterChange: handleMappingFilterChange,
    activeTab,
    appliedUserId: search.userId,
    institutesQueryParams,
    handleTabChange,
  };
}
